const en = {
  // A7. Footer + cookie banner + register-flow consent
  footer: {
    impressum: 'Imprint',
    datenschutz: 'Privacy',
    agb: 'Terms',
    widerruf: 'Right to withdraw',
    faq: 'FAQ',
  },
  cookies: {
    bannerLabel: 'Cookie settings',
    message: 'We use strictly necessary cookies to keep you logged in. Optionally, with your consent, we load Sentry for error diagnostics.',
    learnMore: 'Learn more',
    settings: 'Settings',
    declineAll: 'Only necessary',
    acceptAll: 'Accept all',
    settingsTitle: 'Cookie settings',
    essential: 'Necessary (always on)',
    essentialDesc: 'Login and session management. The app does not work without these.',
    analytics: 'Error monitoring (Sentry)',
    analyticsDesc: 'Helps us spot bugs. Sends anonymized stack traces to Sentry.',
    marketing: 'Marketing',
    marketingDesc: 'Currently unused. If we ever add pixels or remarketing, we ask here.',
    saveChoices: 'Save choices',
  },
  // (auth.* keys merged below into the canonical block. This comment
  // marker is kept so future greps can find the consolidated home.)
  // A5. In-app banner that mirrors the preview email. Surfaces
  // pending renders waiting on the user's decision.
  pendingPreview: {
    // require_approval mode (silence == skip)
    singleTitle: 'One preview waiting for your approval',
    multiTitle: '{{count}} previews waiting for your approval',
    multiSubtitle: "We've rendered your auto-posts. Take a look before they go out.",
    review: 'Review preview',
    reviewNext: 'Review next',
    // auto_with_preview mode (silence == post). Copy must NOT imply
    // approval is required. The post fires anyway. The user only
    // opens this if they want to tweak or stop it.
    autoSingleTitle: 'Preview for your scheduled auto-post',
    autoMultiTitle: '{{count}} auto-posts ready to preview',
    autoSubtitleScheduled: 'Will be posted automatically at {{time}}',
    autoSubtitleNoTime: 'Will be posted automatically. Look in only if you want to tweak it.',
    autoMultiSubtitle: 'Will be posted automatically. Open only if you want to tweak or stop one.',
    autoReview: 'View or stop',
    autoReviewNext: 'View next',
  },
  // A5. Auto-post preview landing page (public, token-gated link)
  preview: {
    title: 'Auto-post preview',
    untitled: 'Your listing',
    scheduledFor: 'Scheduled: {{date}}',
    captionLabel: 'Caption',
    approve: 'Approve to post',
    reject: 'Skip this post',
    stop: 'Stop this post',
    noActionHelp: "If you do nothing, we'll post automatically at the scheduled time.",
    noActionHelpStrict: "Without your approval, this post will NOT be published.",
    unsavedEdits: "You have unsaved edits. \"Approve\" saves them automatically.",
    unsavedEditsAuto: "You have unsaved edits. Save them before the video is auto-posted.",
    approvedTitle: "Approved. We'll post at the scheduled time",
    approvedHelp: 'You can close this tab. Status is on the dashboard if anything looks off.',
    rejectedTitle: 'Post skipped',
    rejectedHelp: "We'll skip this slot. Tomorrow we'll send you a new suggestion.",
    publishNow: 'Publish now',
    publishingNowTitle: 'Publishing now',
    publishingNowHelp: "We're posting this video to your connected platforms now. Your next auto-post will arrive by email tomorrow as usual.",
    alreadyPostedTitle: 'Already posted',
    alreadyPostedHelp: "This video is already published. Nothing to do. Your next auto-post will arrive by email as usual.",
    slotPassedTitle: 'Slot already passed',
    slotPassedHelp: "This slot expired without a confirmation. We skipped the post. The next one comes tomorrow.",
    slotPassedTip: "Tip: switch to \"Auto with preview\" in settings so missed confirmations don't stop the post.",
    alreadyRejectedTitle: 'Already cancelled',
    alreadyRejectedHelp: "You cancelled this post earlier. It won't be published. Tomorrow we'll send a fresh suggestion.",
    renderFailedTitle: 'Render failed',
    renderFailedHelp: "We couldn't create this video. We'll try tomorrow with a different listing. If this keeps happening, email support@real-content.at.",
    missingToken: 'No token in the URL.',
    loadFailed: 'Preview could not be loaded. The link is expired or invalid.',
    actionFailed: 'Action failed. Please try again later.',
    videoUnavailable: 'Video not ready yet. Try again in a few minutes.',
    // Navigation footer. Appears below every state so the user has
    // a way back to the app from this email-landing page.
    backToDashboard: 'Back to dashboard',
    manageAutoPost: 'Manage auto-post',
    signIn: 'Sign in to Real Content',
    // Fast-track edit / re-render from the preview page
    fastTrackTitle: 'Not quite right? Adjust the video',
    optionsFailed: "Couldn't load options.",
    rerenderStarted: 'Creating a new video …',
    rerenderFailed: 'Re-render failed.',
    rerendering: 'Creating a new video …',
    rerenderingHint: 'This usually takes under a minute.',
    template: 'Template',
    property: 'Property',
    tooFewImages: 'too few images',
    imagesShort: 'images',
    comboLoading: 'Updating images & texts …',
    images: 'Images',
    imagesHint: 'Order = order in the video.',
    imagesCountHint: 'Please pick exactly {{count}} images.',
    onScreenText: 'On-screen text',
    voiceoverScript: 'Voiceover script',
    voiceoverPlaceholder: 'What the AI voice says …',
    voiceoverOn: 'On',
    voiceoverOffLabel: 'Off',
    voiceoverOff: 'No voiceover — the video gets background music only.',
    music: 'Music',
    musicAutomatic: 'Automatic (from your favorites)',
    applyEdits: 'Apply & re-render',
    differentPropertyHint: "This property doesn't fit at all?",
    differentProperty: 'Different property',
  },
  // A4. Anti-abuse gating banner + verify-email landing page
  verify: {
    emailTitle: 'Please confirm your email address',
    emailHelp: "We've sent you a confirmation link. Until you click it, you can't generate videos.",
    resend: 'Resend confirmation',
    resendSent: "We've sent a new confirmation email. Check your inbox (and spam).",
    resendThrottled: 'Too many requests. Please wait a few minutes and try again.',
    resendFailed: 'Could not send the email.',
    cardTitle: 'Add a card. Free, no charge',
    cardHelp: "We just verify the card is real (€0 authorization). Nothing is charged. We need this before generating your first video. It stops abuse.",
    cardCta: 'Add card',
    cardSetupFailed: 'Could not open card verification.',
  },
  verifyEmail: {
    verifying: 'Verifying…',
    verifyingHelp: 'Checking your confirmation link. One moment.',
    success: 'Email verified!',
    successHelp: 'Your account is active. You can start now.',
    errorTitle: 'Verification failed',
    errorHelp: 'Log in and click "Resend confirmation" on the dashboard to get a new link.',
    failed: 'Verification failed. The link is expired or invalid.',
    missingToken: 'No token in the URL.',
    goToDashboard: 'Go to dashboard',
    goToLogin: 'Go to login',
  },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    next: 'Next',
    back: 'Back',
    skip: 'Skip',
    loading: 'Loading...',
    saving: 'Saving…',
    error: 'Error',
    success: 'Success',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    confirm: 'Confirm',
    logout: 'Sign Out',
    upgrade: 'Upgrade',
    toDashboard: 'To Dashboard',
    uploading: 'Uploading…',
    remove: 'Remove',
    retry: 'Retry',
    refresh: 'Refresh',
    search: 'Search',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    or: 'or',
    add: 'Add',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Could not copy.',
    connect: 'Connect',
    connected: 'Connected',
    notConnected: 'Not connected',
    activate: 'Activate',
    deactivate: 'Deactivate',
    active: 'Active',
    inactive: 'Inactive',
    configure: 'Configure',
    generate: 'Generate',
    import: 'Import',
    upload: 'Upload',
    settings: 'Settings',
    details: 'Details',
    status: 'Status',
  },
  nav: {
    dashboard: 'Dashboard',
    autoPost: 'Auto Post',
    properties: 'Properties',
    profile: 'Profile',
    social: 'Social Media',
    videos: 'Videos',
    subscription: 'Plan',
    justimmo: 'Justimmo',
    imports: 'Sources',
    language: 'Language',
    team: 'Team',
    // Section headers in the mobile drawer
    primary: 'Main menu',
    secondary: 'Account & Tools',
    // aria-labels for icon-only nav buttons (mobile)
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    logoToDashboard: 'Real Content. Back to dashboard',
    studio: 'AI Studio',
    soon: 'Soon',
  },
  // AI Studio teaser page (/studio). The studio is not live yet.
  studio: {
    kicker: 'Coming soon',
    title: 'AI Studio',
    subtitle: 'Get more out of your listing photos: enhance them, turn a single photo into a short clip, and decide yourself what goes into your video.',
    demoTitle: 'One photo becomes a clip',
    demoPhoto: 'Listing photo',
    demoClip: 'AI clip, 5 seconds',
    demoNote: 'Example from our tests. You always see your own results as a preview first.',
    feature1Title: 'Enhance photos',
    feature1Body: 'Light, colour and sharpness fixed in seconds, so every room looks as bright as it really is.',
    feature2Title: 'Clips from photos',
    feature2Body: 'A still photo becomes a calm camera move in portrait format. A great opener for Reels and TikTok.',
    feature3Title: 'You stay in control',
    feature3Body: 'Nothing happens automatically. You see every result first and decide whether it gets used.',
    pricingNote: 'A monthly allowance is included in the paid plans. If you need more, you simply top up.',
    ctaTitle: 'Want early access?',
    ctaBody: "We're looking for agents who try the AI Studio before launch and tell us what they actually need.",
    ctaButton: 'I want early access',
    ctaDone: "You're on the list. We'll email you as soon as you can try it.",
    ctaToast: "Thanks, you're on the list.",
    ctaError: "That didn't work. Please try again.",
  },
  // Generated-video card on the property page (per-render result)
  videoCard: {
    unknownTemplate: 'Unknown template',
    captionCopied: 'Caption copied',
    copyFailed: 'Could not copy',
    copyAria: 'Copy caption',
    copyMenuHint: 'Copy a per-platform tuned caption',
    copyForPlatform: 'Copy for…',
  },
  starterTier: {
    label: 'Starter',
    videosUsed: 'This month: {{count}} / {{max}} videos',
    upgradeCta: 'Upgrade to Autopilot',
  },
  freeTier: {
    label: 'Free Plan',
    videosUsed: '{{count}} / {{max}} videos used',
    upgradeUnlimited: 'Upgrade to Autopilot',
  },
  auth: {
    welcomeBack: 'Welcome back',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    signInSubtitle: 'Sign in to your account',
    signUpSubtitle: 'Create your account',
    email: 'Email',
    password: 'Password',
    fullName: 'Full Name',
    noAccount: 'Don\'t have an account?',
    hasAccount: 'Already have an account?',
    loginSuccess: 'Welcome back!',
    registerSuccess: 'Account created successfully!',
    logoutSuccess: 'Signed out successfully',
    authFailed: 'Authentication failed',
    // GDPR consent block on register form. Each fragment renders
    // around inline links to /agb and /datenschutz, so kept short.
    termsConsentPrefix: 'I have read the',
    termsLink: 'Terms',
    termsAnd: 'and the',
    privacyLink: 'Privacy Policy',
    termsConsentSuffix: 'and accept them.',
    acceptTermsRequired: 'Please accept Terms and Privacy Policy.',
    // Tagline + logo aria-label on the auth page.
    tagline: 'Automate your real estate social media',
    logoToHome: 'Real Content. Back to home',
    // Inline password-policy feedback on the Sign-Up form.
    passwordRuleLength: 'At least 10 characters',
    passwordRuleLetter: 'One letter',
    passwordRuleDigit: 'One digit',
    passwordMissing: 'Your password still needs: {{rules}}.',
    // Show/hide-password toggle (eye icon inside the input). aria-only;
    // no visible text. The icon flips between Eye / EyeOff.
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    // Form-field placeholders (separate key so we can localise the
    // example name without touching the label).
    fullNamePlaceholder: 'John Doe',
    // ── Forgot-password sub-flow on AuthPage ──────────────────────
    forgotLink: 'Forgot password?',
    forgotTitle: 'Forgot password',
    forgotSubtitle: "We'll send you a link to reset it.",
    forgotSubmit: 'Send reset link',
    forgotSentSubtitle: 'Check your inbox.',
    forgotSentBody: "If an account with this email exists, we've sent you a reset link. It's valid for 30 minutes.",
    forgotSentHint: "No email? Check your spam folder or try again in a few minutes.",
    forgotRateLimit: 'Too many requests. Please wait an hour and try again.',
    backToLogin: 'Back to sign-in',
  },
  // /reset-password page. Consumes the token from the email link
  // and lets the user set a new password.
  resetPassword: {
    title: 'Choose a new password',
    subtitle: "Pick a new password for your account. You'll be sent to sign-in after.",
    newPasswordLabel: 'New password',
    confirmLabel: 'Confirm password',
    mismatch: "Passwords don't match.",
    submit: 'Save password',
    submitting: 'Saving…',
    successTitle: 'Password changed',
    successBody: 'You can now sign in with your new password.',
    successToast: 'Password changed.',
    toLogin: 'Go to sign-in',
    errorTitle: 'Link invalid or expired',
    errorBody: 'Please request a new link.',
    failed: 'Something went wrong. Try again.',
    missingToken: 'No token found in the URL.',
    requestNew: 'Request a new link',
  },
  // Illustrated step-by-step Justimmo Export-API setup guide. Used by
  // both the onboarding ImportStep and the standalone JustimmoPage.
  justimmoGuide: {
    title: 'Show step-by-step setup guide',
    subtitle: '8 screenshots from Justimmo. About 3 minutes.',
    adminWarning: 'Note: you need admin rights in Justimmo, otherwise the "Exports" item won\'t show up.',
    footer: 'Click any image to open it full-size in a new tab.',
    steps: {
      menu: {
        title: 'Click "More" in the top navigation',
        body: 'Log in as administrator. In the top nav, open the "More" menu.',
      },
      exports: {
        title: 'Select "Exports"',
        body: 'In the submenu, navigate to "Exports". That\'s where all external integrations live.',
      },
      create: {
        title: 'Click "Create API"',
        body: 'Top right, click the "Create API" button to start a new API user.',
      },
      autoAdd: {
        title: 'Enable "Auto-add new properties"',
        body: 'Turn this on so every new listing flows into Real Content without an extra click. Leave it off if you prefer to opt-in per property.',
      },
      category: {
        title: 'Category "Other", fill name + email, "Save"',
        body: 'Set the category to "Other", give it a recognisable name like "Real Content" and your email, then save.',
      },
      open: {
        title: 'Open the new "JUSTIMMO API" entry',
        body: 'Click the freshly created entry in the list to reveal username + password.',
      },
      creds: {
        title: 'Paste username + password into Real Content',
        body: 'Copy both values and drop them into the fields below. Then hit "Connect".',
      },
      enable: {
        title: 'Per property: "Marketing" → bottom → tick "Justimmo API"',
        body: 'If no listings show up: open each property, scroll to the bottom of the "Marketing" tab, and tick the "Justimmo API" checkbox. With auto-add enabled this happens automatically for new properties.',
      },
    },
  },
  onboarding: {
    welcome: 'Welcome to Real Content',
    subtitle: 'Set up automatic social media posts in just a few steps',
    letsGo: 'Let\'s Go',
    stepOf: 'Step {{current}} of {{total}}',
    // Toast errors when WelcomeStep loads / saves the user profile.
    profileLoadFailed: 'Could not load profile.',
    profileSaveFailed: 'Could not save profile.',
    step1: {
      title: 'Your Profile',
      subtitle: 'This information will appear in your videos',
      name: 'Your Name',
      namePlaceholder: 'John Doe',
      brandName: 'Company Name',
      brandPlaceholder: 'Doe Real Estate',
      phone: 'Phone Number',
      phonePlaceholder: '+1 (555) 123-4567',
      photo: 'Your Photo',
      photoDesc: 'Appears in every video\'s endcard. Without a photo we use a placeholder.',
      logo: 'Company Logo',
      logoDesc: 'Appears in every video\'s endcard.',
      optionalHeading: 'For more personal videos (recommended)',
      optionalHint: 'Without these we use placeholders in the endcard.',
      optionalTag: 'optional',
      photoRecommended: 'Recommended. Placeholder otherwise',
    },
    step2: {
      title: 'Import Properties',
      subtitle: 'Connect Justimmo or add properties manually',
      justimmoTitle: 'Connect Justimmo Export',
      username: 'API Username',
      password: 'API Password',
      connectButton: 'Connect',
      connected: 'Justimmo connected',
      importAll: 'Import all',
      importSelected: 'Import {{count}}',
      importing: 'Importing...',
      imported: '{{count}} properties imported',
      noProperties: 'No properties found',
      skipManual: 'Skip - add manually later',
      propertiesFound: '{{count}} properties found',
      loadFailed: 'Could not load properties',
      enterCredentials: 'Please enter username and password',
      connectionFailed: 'Connection failed',
      alreadyImported: 'Property already imported',
      importedSingle: 'Imported: {{title}}',
      importFailed: 'Import failed',
      inst1: 'Log into Justimmo as administrator',
      inst2: 'Navigate to "More" \u2192 "Exports"',
      inst3: 'Select "Custom Exports" tab',
      inst4: 'Click "Create new API export"',
      inst5: 'Copy username and password',
      // Agent picker. Multi-tenant safety, mandatory before listing
      // properties so we never import a colleague's listing under your name.
      agentPickerTitle: 'Which agent are you?',
      agentPickerHelp: 'Justimmo returns every property in your agency. Pick yourself here so we only import your listings. Never your colleagues’.',
      agentPickerPlaceholder: 'Please pick…',
      agentPicked: 'Profile saved. We’ll only show your own listings.',
      agentBackfilled: 'Linked {{count}} existing properties to your profile.',
      agentPickFailed: 'Could not save your profile.',
      pickAgentFirst: 'Pick yourself from the team list above to load your properties.',
      teamEmpty: 'We couldn’t load the team list. Make sure your Justimmo API account has “team/list” permission, or message us.',
    },
    // Step 3: swipe like/dislike on rendered samples (`SampleSwipeStep.js`).
    step3: {
      title: 'Pick your style',
      subtitle: 'Every template was rendered with your photos. Like the ones you love. We\'ll use them for your auto-posts.',
      preparing: 'Rendering your templates…',
      preparingDesc: 'We\'re rendering one sample per template in parallel with your photos. The first card is ready in a minute or two. You can start swiping right away.',
      noPropertyTitle: 'No property yet',
      noPropertyBody: 'Import a property in the previous step so we can render real previews for you.',
      fewPhotosTitle: 'Not enough photos',
      fewPhotosBody: 'Your property doesn\'t have enough photos yet for the sampler. Add a few more and come back.',
      startFailedTitle: 'Couldn\'t start',
      startFailedBody: 'We couldn\'t kick off the sample renders. Please try again.',
      alreadyDone: 'You\'ve already completed onboarding.',
      freeTierBanner: 'You\'re trying 5 templates. Upgrade to see all 15.',
      skipStep: 'Skip this step',
      doneTitle: 'All swiped!',
      doneSubtitle: 'You liked {{count}} templates. We\'ll use them for your auto-posts.',
      noLikesHint: 'No worries. If nothing caught your eye we\'ll let the AI pick a template automatically per auto-post. You can change favorites anytime.',
      ofReady: 'of',
      readyLabel: 'templates rendered',
      renderingHint: 'Go ahead and swipe. The rest render in the background.',
    },
    // Step 4: background music selection (`MusicStep.js`).
    step4: {
      title: 'Pick your music',
      subtitle: 'Plays in the background of your auto-posts. Change it anytime.',
      // Multi-select variant of the subtitle. Used when the user can
      // like multiple presets and we'll randomise per post.
      subtitleMulti: 'Like everything you enjoy. We\'ll randomly pick one of your favourites for each auto-post.',
      selectedCount: '{{count}} selected',
      play: 'Preview',
      pause: 'Pause',
      playFailed: 'Could not play preview audio.',
      loadError: 'Couldn\'t load presets. You can upload your own or skip this step.',
      uploadTitle: 'Use your own music',
      uploadSubtitle: 'Upload an MP3 if you have a specific brand track.',
      // Multi-select variant: uploaded MP3 joins the random rotation.
      uploadSubtitleMulti: 'Upload an MP3. It joins the random rotation.',
      uploadBtn: 'Upload',
      uploadSuccess: 'Custom music uploaded',
      uploadFailed: 'Upload failed. Please try again.',
      noMusic: 'Use no music',
    },
    // Step 5: AI voiceover yes/no (`VoiceoverStep.js`).
    step5: {
      title: 'Use the AI voice?',
      subtitle: 'You just heard the voice in the samples. Want to keep it on your auto-posts?',
      subtitleWithPreview: 'Take a quick listen to the AI voice. Then decide whether it should run on your auto-posts.',
      tapToHear: 'Hear the voice',
      muted: 'Mute',
      yesTitle: 'Yes, keep voiceover',
      yesBody: 'We\'ll read out your listings with your name and brand name. Good for "new listing" posts.',
      noTitle: 'Music only',
      noBody: 'No voiceover. Posts are quieter and often feel more natural on TikTok/Reels.',
      footer: 'You can change this anytime from the auto-post settings.',
      // Voice picker (only renders when "yes" is selected).
      voicePickerTitle: 'Pick a voice',
      voicePickerHint: 'Hold to listen',
      playSample: 'Sample',
      stop: 'Stop',
      holdToHear: 'Hold to listen',
      holding: 'Holding…',
      // Advanced expander. Power-users paste their own ElevenLabs voice_id.
      advancedTitle: 'Advanced: custom voice',
      advancedHelp: 'Have your own ElevenLabs voice (e.g. a voice clone)? Paste the voice_id here to use it.',
      advancedFootnote: 'You can find the voice_id in your ElevenLabs Voice Library dashboard.',
    },
    // Step 6: connect social accounts (moved up from old step 5).
    step6: {
      title: 'Socials',
      subtitle: 'Connect your accounts for automatic posting',
      subtitleAll: 'Connect every account where your auto-posts should land. Instagram, Facebook, TikTok, YouTube, X, and LinkedIn.',
      connectInstagram: 'Connect Instagram',
      connectFacebook: 'Connect Facebook',
      connectAll: 'Connect accounts',
      connectDesc: 'Click Connect to link your Instagram, Facebook, TikTok, YouTube, X, and LinkedIn accounts',
      connectedAccounts: '{{count}} account(s) connected',
      popupBlocked: 'Popup was blocked. Please allow popups for this site.',
    },
    // Step 7: activate auto-post (moved up from old step 6).
    step7: {
      title: 'Activate',
      subtitle: 'Configure your automatic posting schedule',
      postsPerDay: 'Posts per day',
      preferredTime: 'Preferred time',
      timezone: 'Timezone',
      summary: 'Summary',
      summaryProfile: 'Profile set up',
      summaryProperties: '{{count}} properties',
      summaryTemplates: '{{count}} favorite templates',
      summarySocials: '{{count}} platform(s) connected',
      activateAutoPost: 'Activate Auto Post now',
      skipActivation: 'Activate later',
      congratulations: 'All set up!',
      congDesc: 'Your Auto Mode is ready. Real Content will create and post videos automatically.',
      activated: 'Auto Post activated!',
      activationFailed: 'Activation failed',
      goToDashboard: 'Go to Dashboard',
    },
  },
  dashboard: {
    title: 'Dashboard',
    autoPostStatus: 'Auto Post Status',
    autoPostActive: 'Auto Post Active',
    autoPostInactive: 'Auto Post Inactive',
    configure: 'Configure',
    nextPost: 'Next Post',
    postsPerDay: '{{count}} video(s) per day at {{time}}',
    noPostsScheduled: 'No posts scheduled',
    stats: {
      properties: 'Properties',
      videos: 'Videos created',
      platforms: 'Platforms',
      weeklyPosts: 'Posts this week',
    },
    recentPosts: 'Recent Posts',
    viewAll: 'View all',
    noRecentPosts: 'No posts yet',
    queue: 'Queue',
    generateNow: 'Generate now',
    queueEmpty: 'No scheduled posts',
    queueCount: '{{count}} posts scheduled',
    loadFailed: 'Could not load dashboard',
    setupRequired: 'Setup required',
    setupDesc: 'Complete the setup to start posting automatically.',
    startSetup: 'Start setup',
  },
  autoPost: {
    title: 'Auto Post Mode',
    modeRecommended: 'Recommended',
    sourcesTitle: 'Data sources',
    sourcesDesc: 'Auto-post uses your imported properties. Manage sources under "Data sources".',
    noSourcesYet: 'No source connected yet',
    manageSources: 'Manage',
    connectSource: 'Connect a source',
    enableWithoutProperties: 'You need at least one property first. Connect Justimmo or Willhaben under "Data sources".',
    // Skip-reason banner. Surfaces "why didn't yesterday's auto-post fire"
    // (audit #28). Reason codes come from scheduler_service._stamp_skip_reason.
    skipBanner: {
      title: 'Last auto-post skipped',
      noProperties: 'No eligible properties found. Make sure your Justimmo or Willhaben filter is set to your own listings.',
      insufficientImages: '"{{title}}" has only {{have}} images; the chosen template needs {{need}}. Add more images or pick a different favorite template.',
      generic: 'Could not create auto-post. Check the queue below for details.',
    },
    // Music section on the AutoPost settings page. Mirrors the onboarding
    // MusicStep semantics: multi-select preset cards + optional MP3
    // upload, scheduler picks one at random per render.
    music: {
      title: 'Background music',
      subtitle: 'Pick your favorite tracks. Each auto-post randomly uses one. So videos don\'t all sound the same.',
      selectedCount: '{{count}} selected',
      play: 'Preview',
      pause: 'Pause',
      playFailed: 'Could not play preview audio.',
      loadError: 'Could not load presets. Reload the page or upload your own file.',
      uploadTitle: 'Add your own music',
      uploadSubtitle: 'Upload an MP3. Joins the random rotation.',
      uploadBtn: 'Upload MP3',
      uploadSuccess: 'Your music was uploaded.',
      uploadFailed: 'Upload failed. Please try again.',
      clearAll: 'Use no music',
    },
    // Caption style. Phase 1-3 of the caption-quality work. See the
    // DE translations for full notes; this is the EN mirror.
    captionStyle: {
      title: 'Caption style',
      subtitle: 'How should the AI write your captions? Each platform gets its own variant.',
      preview: 'Preview',
      toneLabel: 'Tone of voice',
      toneHint: "Shapes the AI's voice across all platforms.",
      tone: {
        professional: 'Professional',
        casual:       'Casual',
        luxury:       'Luxury',
        friendly:     'Friendly local',
      },
      ctaLabel: 'Call-to-action',
      ctaHint: 'Engagement (tag/save/comment) beats website-clicks for organic reach.',
      cta: {
        engagement: 'Engagement (tag, save, DM)',
        website:    'Website / profile',
        dm:         'Direct message',
      },
      hashtagLabel: 'Hashtag count',
      hashtagHint: 'More ≠ better. "Standard" is based on 2026 research.',
      aggression: {
        conservative: 'Sparing',
        standard:     'Standard',
        aggressive:   'Max',
      },
      emojiLabel: 'Emoji density',
      emojiHint: 'LinkedIn never gets emojis. Regardless of this setting.',
      emoji: {
        none:     'None',
        sparing:  'Sparing',
        moderate: 'Moderate',
        generous: 'Playful',
      },
      voiceTitle: 'Teach the AI your writing style',
      voiceLearned: 'Writing style saved',
      voiceHint: "We'll study your past social posts so future captions sound like you wrote them. Word choice, tone, typical structures.",
      voiceAnalyze: 'Analyze style',
      voiceUpdate: 'Re-analyze',
      voiceClear: 'Clear style',
      advancedToggle: 'Advanced (hashtags, custom instructions)',
      customTagsLabel: 'Always include',
      customTagsHint: 'Your branded tags or city tags every post should carry.',
      customTagsPlaceholder: '#MyBrand',
      blockedTagsLabel: 'Never use',
      blockedTagsHint: 'Tags the AI might generate that you don\'t want.',
      blockedTagsPlaceholder: '#TagIDislike',
      customInstructionsLabel: 'Custom instructions',
      customInstructionsHint: 'Injected into every AI prompt. Example: "Always mention the energy certificate status".',
      customInstructionsPlaceholder: 'e.g. "Avoid buzzwords like exclusive or unique. Always mention that pets are welcome."',
    },
    brandVoice: {
      title: 'Analyze your writing style',
      why: "AI captions sound generic by default. Give us some of your past posts (or auto-import them from your connected profile) and future captions will sound like you wrote them. Word choice, tone, structure.",
      autoImportBtn: 'Import from connected profile',
      autoImportRunning: 'Importing your posts…',
      autoImportFailed: 'Auto-import failed. Connect a social account under "Social Media" or paste posts manually.',
      autoImportNotEnough: "We didn't find enough usable posts in your profile. Please paste manually.",
      autoImportNoSocial: 'Connect a social account first so we can import your posts.',
      autoImportSuccess: 'Imported {{count}} posts from {{platforms}}.',
      orPasteManually: 'Or paste posts manually:',
      captionsLabel: 'Your past posts (3-10)',
      captionsHint: 'Separate posts with a blank line or "---". Captions, tweets, LinkedIn posts. Anything that shows your style.',
      captionsPlaceholder: 'Example:\n\nGorgeous 3-bedroom in Brooklyn! Tag someone in the comments who\'d love it.\n\n---\n\nJust spotted: pre-war loft in Tribeca, 12-ft ceilings. Stunning. Move fast. This won\'t sit.',
      zero: 'No posts detected yet.',
      detected: '{{count}} posts detected.',
      needMore: 'Need at least 2 posts.',
      languageLabel: 'Analysis language:',
      analyze: 'Analyze style',
      analyzing: 'Analyzing…',
      analyzeFailed: 'Analysis failed. Please try again later.',
      notEnough: "Couldn't detect enough pattern. Add more posts?",
      currentlyLearned: 'Currently saved:',
      replaceHint: 'A new analysis replaces the current style. To just tweak it, cancel.',
      gotIt: 'Your style:',
      tone: 'Tone',
      hookStyle: 'Hook style',
      ctaStyle: 'CTA style',
      structure: 'Structure',
      signaturePhrases: 'Recurring phrases',
      avoid: 'You avoid',
      emojiDensity: 'Emoji density',
      summary: 'Summary',
      editFields: 'Edit fields',
      editHint: 'The AI read your style from your posts. Tweak anything before accepting.',
      addPhrase: 'Add phrase',
      addAvoid: 'Add avoid',
      retry: 'Different posts',
      accept: 'Use this style',
      acceptHint: "Auto-saved. No need to click Save up top.",
      saved: 'Your writing style is now active for future captions.',
    },
    preview: {
      title: 'Per-platform caption preview',
      why: 'This is what your captions look like on each platform with your current style settings. Try different properties, tune the settings, see results instantly.',
      noProperties: 'No properties available',
      placeholder: 'Pick a property and click "Generate preview" to see captions.',
      generate: 'Generate preview',
      regenerate: 'Regenerate',
      generating: 'Generating…',
      failed: 'Preview generation failed. Please try again.',
      // Empty state when user has zero properties to preview against
      emptyTitle: 'Add a property first',
      emptyHint: 'Caption preview needs a property to work from. Connect Justimmo, import from Willhaben, or add one manually.',
      emptyCta: 'Go to my properties',
    },
    subtitle: 'Configure automatic daily posting',
    enabled: 'Auto Post Active',
    disabled: 'Auto Post Inactive',
    schedule: 'Schedule',
    scheduleDesc: 'When should posts be published?',
    time: 'Time',
    timezone: 'Timezone',
    language: 'Language',
    platforms: 'Platforms',
    platformsDesc: 'Where should posts be published?',
    minOnePlatform: 'At least one platform must be selected',
    templates: 'Favorite Templates',
    templatesDesc: 'AI prefers these templates for automatic selection',
    autoSelectTemplate: 'AI selects best template automatically',
    aiSettings: 'AI Settings',
    aiSettingsDesc: 'Configure AI content generation',
    autoCaptions: 'Auto-generate captions',
    autoCaptionsDesc: 'AI writes engaging captions for each post',
    autoHashtags: 'Auto-generate hashtags',
    autoHashtagsDesc: 'AI adds relevant hashtags for more reach',
    enableVoiceover: 'Enable AI voiceover',
    enableVoiceoverDesc: 'AI generates a spoken voiceover for the video (when supported by template)',
    // A5. Preview mode (3 options, guided progression)
    previewModeLabel: 'Preview mode',
    previewModeIntroTitle: 'How should we post?',
    previewModeIntroDesc: 'We recommend: start with "Approval required". You get a preview email 12 hours before each post and have to actively click "Approve". After a few good posts, when you trust the AI, switch to "Notify only" or "Fully automatic".',
    modeStrictTitle: 'Approval required (recommended to start)',
    modeStrictDesc: 'You get a preview email 12 hours before each post. We ONLY post if you click "Approve". If you ignore the email, we skip the slot.',
    modeNotifyTitle: 'Notify only',
    modeNotifyDesc: 'You get the preview email as usual, but we post automatically. Unless you actively click "Skip". Good for the "I check most of the time but trust the system" phase.',
    modeSilentTitle: 'Fully automatic (no emails)',
    modeSilentDesc: "Set-and-forget: we post without a preview email. Pick this only when you fully trust the AI and don't want notifications anymore.",
    queue: 'Queue',
    queueCount: '{{count}} entries',
    history: 'History',
    noQueue: 'No entries in queue',
    noQueueDesc: 'Click "Generate now" or enable Auto Post',
    noHistory: 'No completed auto-posts yet',
    totalPosts: 'Total Posts',
    successRate: 'Success Rate',
    failed: 'Failed',
    noSocialsWarning: 'No social media accounts connected',
    noSocialsDesc: 'Connect your social media accounts under "Social Media" before enabling Auto Post.',
    connectSocials: 'Connect',
    enableWithoutSocials: 'Please connect your social media accounts first.',
    // v1.6. Hard-gate copy. Toggle is disabled until every item below
    // is in place; the banner lists what's missing.
    gateBannerTitle: 'Setup incomplete. Auto-Post disabled',
    gateBannerTitleSoft: 'Setup gap detected',
    gateMissingName: 'Name missing in profile',
    gateMissingBranding: 'Profile photo or logo missing',
    gateMissingSocials: 'No platforms connected',
    gateMissingProperties: 'No property imported',
    gateTooltipPrefix: 'Setup incomplete:',
    gateBlocked: 'Setup incomplete',
    gateCta: 'Complete setup',
    saved: 'Settings saved!',
    saveFailed: 'Save failed',
    loadFailed: 'Could not load settings',
    discarded: 'Changes discarded.',
    // Sticky sub-nav labels (one per settings section)
    nav: {
      sources:       'Sources',
      schedule:      'Schedule',
      platforms:     'Platforms',
      templates:     'Templates',
      captions:      'Captions',
      voiceover:     'Voiceover',
      notifications: 'Notifications',
      ai:            'AI',
      captionStyle:  'Caption style',
      music:         'Music',
    },
    captionsSectionDesc: 'How the AI writes your captions and hashtags.',
    voiceoverSectionDesc: 'The AI voice that narrates your video.',
    notificationsSectionDesc: 'The preview email you get before each scheduled post.',
    // Floating "unsaved changes" bar
    unsavedBar: {
      title:      'You have unsaved changes',
      titleShort: 'Unsaved changes',
      discard:    'Discard',
      aria:       'Unsaved changes',
    },
    // Detail modal. Opens when user clicks a queue or history row.
    detail: {
      captionsEditable:    'Captions (editable per platform)',
      captionsReadOnly:    'Captions',
      regenerate:          'Regenerate',
      regenerateHint:      'Have the AI write new captions',
      regenerating:        'Generating…',
      regenerated:         'Captions regenerated.',
      regenerateFailed:    'Regeneration failed.',
      regenerateWarn:      'You have unsaved edits. Regenerate anyway? (discards your edits)',
      chars:               'chars',
      videoRendering:      "Video is rendering. Preview will show here when it's ready.",
      saved:               'Captions saved.',
      saveFailed:          'Save failed.',
      approve:             'Approve',
      reject:              'Reject',
      stop:                'Stop',
      approved:            'Post approved. Will publish at the scheduled time.',
      rejected:            'Post cancelled.',
      decisionFailed:      'Action failed.',
      saveBeforeDecision:  'Save changes before approving',
      readOnlyCompleted:   'This post has already been published and can no longer be edited.',
      readOnlyRejected:    'This post was cancelled.',
      readOnlyOther:       'This post is no longer editable.',
      errorLabel:          'Error message',
    },
    enabledDesc: '{{count}} video(s) per day at {{time}}',
    disabledDesc: 'Enable for automatic posting',
    postCreated: 'Post for {{property}} created',
    postCreateFailed: 'Could not create post',
    entryDeleted: 'Entry deleted',
    deleteFailed: 'Delete failed',
    retryStarted: 'Retry started',
    retryFailed: 'Retry failed',
    refreshFailed: 'Refresh failed',
    property: 'Property',
    unknown: 'Unknown',
    photos: '{{count}} photos',
    templatesDescManual: 'Select templates for auto-posting',
    generateNow: 'Generate now',
    status: {
      pending: 'Pending',
      claiming: 'Claiming',
      generating_video: 'Generating video',
      // require_approval mode (silence == skip)
      preview_pending: 'Waiting for your approval',
      // auto_with_preview mode (silence == post)
      preview_pending_auto: 'Preview ready · auto-posts',
      video_ready: 'Video ready',
      posting: 'Posting',
      completed: 'Posted',
      completed_partial: 'Partly posted',
      claiming_post: 'Posting',
      cancelled: 'Called off',
      rejected: 'Cancelled',
      failed: 'Failed',
    },
  },
  properties: {
    autoPostPaused: 'Auto-post paused',
    title: 'Properties',
    subtitle: 'Manage your property listings',
    addProperty: 'Add property',
    addFirst: 'Add Your First Property',
    importJustimmo: 'Import from Justimmo',
    noProperties: 'No properties yet',
    noPropertiesDesc: 'Import properties from Justimmo or add one manually.',
    createFirst: 'Create your first property to start generating content.',
    loadFailed: 'Failed to load properties',
    deleteConfirm: 'Are you sure you want to delete this property?',
    deleted: 'Property deleted',
    rooms: 'Rooms',
    area: 'Area',
    generateVideo: 'Generate video',
    viewDetails: 'View details',
    more: '+{{count}} more',
    // Search + sort + filter
    searchPlaceholder: 'Search by title or address…',
    sortUpdatedDesc: 'Recently updated',
    sortCreatedDesc: 'Recently created',
    sortTitleAsc: 'Title A–Z',
    sortPriceDesc: 'Price high → low',
    sortPriceAsc: 'Price low → high',
    filterAll: 'All',
    filterWithVideo: 'With video',
    filterInRotation: 'Auto-post active',
    filterNoActivity: 'No activity',
    autoPostActive: 'Auto-post active',
    countLine: '{{count}} properties',
    noMatches: 'No matches for "{{query}}".',
    resetFilters: 'Reset filters',
  },
  social: {
    title: 'Social Media',
    subtitle: 'Manage your connected accounts',
    connectAccount: 'Connect account',
    connecting: 'Connecting...',
    connectedAccounts: 'Connected Accounts',
    noAccounts: 'No accounts connected',
    noAccountsDesc: 'Connect your social media accounts to post videos.',
    publish: 'Publish',
    publishing: 'Publishing...',
    publishSuccess: 'Published successfully!',
    publishFailed: 'Publishing failed',
    selectPlatform: 'Select at least one platform',
    publishHistory: 'Publish History',
    connectionFailed: 'Could not start connection',
  },
  imageUpload: {
    invalidType: 'Please upload a JPG, PNG, WebP or GIF image',
    tooLarge: 'Image must be smaller than 5MB',
    uploadFailed: 'Upload failed',
    uploadSuccess: 'Image uploaded successfully!',
    uploadError: 'Could not upload image',
    removed: 'Image removed',
    uploading: 'Uploading...',
    uploaded: 'Image uploaded',
    clickOrDragReplace: 'Click or drag to replace',
    dragOrClick: 'Drag image here or click',
    maxSize: 'JPG, PNG, WebP or GIF (max 5MB)',
    orPasteUrl: 'Or paste an image URL directly',
  },
  timezones: {
    vienna: 'Vienna (CET)',
    berlin: 'Berlin (CET)',
    zurich: 'Zurich (CET)',
    london: 'London (GMT)',
    newYork: 'New York (EST)',
  },
  profile: {
    title: 'Profile',
    subtitle: 'Your info appears in video outros',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    brandName: 'Company Name',
    photo: 'Profile Photo',
    logo: 'Company Logo',
    saved: 'Profile saved!',
    saveFailed: 'Save failed',
    uploadPhoto: 'Upload photo',
    uploadLogo: 'Upload logo',
    dragDrop: 'Drag a file here or click to upload',
    maxSize: 'Max 5 MB (JPG, PNG, WebP)',
    // GDPR controls (Art. 17 + 20)
    dataAndAccountTitle: 'Data & account',
    dataAndAccountSubtitle: 'Download all your data as a ZIP (GDPR Art. 20) or permanently delete your account (GDPR Art. 17).',
    exportTitle: 'Download my data',
    exportHelp: 'ZIP with every record we store on your account. API tokens and passwords are excluded.',
    exportButton: 'Download ZIP',
    exportDone: 'Data exported.',
    exportFailed: 'Export failed.',
    deleteTitle: 'Delete account',
    deleteHelp: 'Marks your account for deletion. You will be logged out and after 7 days all data is permanently removed. Email us within 7 days to undo.',
    deleteButton: 'Delete account',
    deleteConfirmWord: 'DELETE',
    deletePrompt: 'Type {{word}} to confirm deletion. You then have 7 days to undo via email.',
    deleteScheduled: 'Account scheduled for deletion. Logging you out now.',
    deleteCancelled: 'Deletion cancelled.',
    deleteFailed: 'Deletion failed.',
  },
  gallery: {
    title: 'Video Gallery',
    subtitle: 'All your generated real estate videos',
    noVideos: 'No videos yet',
    noVideosDesc: 'Videos will appear here once they are generated.',
    downloadVideo: 'Download Video',
    viewProperty: 'View Property',
    createProperty: 'Create Property',
    generationFailed: 'Generation failed',
    generating: 'Video is being generated...',
    noFilteredVideos: 'No {{filter}} videos',
    noFilteredDesc: 'You don\'t have any {{filter}} videos at the moment',
    createFirst: 'Create properties and generate videos to see them here',
    filter: {
      all: 'All',
      ready: 'Ready',
      processing: 'Processing',
    },
    // Date-bucketed sections
    bucketToday: 'Today',
    bucketThisWeek: 'This week',
    bucketOlder: 'Older',
    goToProperty: 'View property',
  },
  // Modal for manually creating a property (replaces /properties/new)
  newProperty: {
    title: 'New property',
    tabBroker: 'Broker profile',
    tabWillhaben: 'Single listing',
    tabManual: 'Manual',
    imagesLabel: 'Images',
    uploading: 'Uploading…',
    dragOrClick: 'Drag images here or click',
    imageHint: 'JPG, PNG, WebP, GIF · max 5 MB per file · multiple ok',
    coverBadge: 'Cover',
    showUrlPaste: 'Or paste image URLs',
    hideUrlPaste: 'Hide URL input',
    titleLabel: 'Title',
    titlePlaceholder: 'Beautiful family home in Vienna',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Charming property in a sought-after location…',
    priceLabel: 'Price',
    locationLabel: 'Address',
    highlightsLabel: 'Highlights',
    highlightPlaceholder: 'e.g. 4 rooms · 130 m² · garden',
    addHighlight: 'Add highlight',
    create: 'Create property',
    // ICU plural shape. T() picks the matching key based on params.count
    imagesUploaded: {
      one: 'Image uploaded.',
      other: '{{count}} images uploaded.',
    },
    fillRequired: 'Please fill in all required fields.',
    imagesRequired: 'At least one image is required.',
    created: 'Property created.',
    createFailed: 'Could not create property.',
  },
  pricing: {
    title: 'Pricing',
    subtitle: 'Choose Your Plan',
    subtitlePro: 'You\'re on the Autopilot plan. Thank you for your support!',
    subtitleFree: 'Autopilot takes posting off your hands',
    free: 'Free',
    pro: 'Professional',
    monthly: '/ month',
    currentPlan: 'Current Plan',
    selectPlan: 'Select Plan',
    downgrade: 'Downgrade',
    upgradeNow: 'Upgrade to Pro',
    redirecting: 'Redirecting...',
    recommended: 'Recommended',
    active: 'Active',
    perfectStart: 'Perfect to get started',
    forProfessionals: 'We post for you. Daily, fully automatic',
    videosUsed: 'Videos used: {{used}} / {{max}}',
    renews: 'Renews: {{date}}',
    manageSubscription: 'Manage subscription',
    portalFailed: 'Could not open the billing portal.',
    questions: 'Questions? Contact us at support@real-content.at',
    // A9. Solo + Team tier copy
    month: 'month',
    comingSoon: 'Coming soon',
    forAgencies: 'For real-estate agencies with multiple agents',
    teamBaseHint: '2 users included · each additional user +€89 / month',
    teamFeature1: 'Everything in Autopilot, for every team member',
    teamFeature2: 'Multi-account management',
    teamFeature3: 'Each agent gets their own Justimmo profile',
    teamFeature4: 'Centralized billing on one account',
    teamFeature5: 'Add/remove members anytime. Billed pro-rata',
    // v1.5. Team CTAs on PricingPage. The button text changes based
    // on the user's current tier.
    startTeam: 'Start Team',
    upgradeToTeam: 'Upgrade to Team',
    notifyMe: 'Notify me when available',
    // v1.8. Starter tier (€29 / 5 videos / no automation)
    forCasual: 'You create videos and post them yourself',
    startStarter: 'Choose Starter',
    upgradeToSolo: 'Upgrade to Autopilot',
    switchInPortal: 'Switch via billing portal',
    videosUsedMonthly: 'This month: {{used}} / {{max}} videos',
    switchToStarter: 'Switch to Starter',
    downgradeFree: 'Switch to Free',
    freeFrom: 'Free from {{date}}',
    cancelScheduled: 'Subscription ends {{date}}, then Free',
    resumeSub: 'Keep subscription',
    planChanged: 'Plan changed.',
    planChangeFailed: 'Plan change failed.',
    confirmUpgrade: 'Switch to Autopilot? The difference for the current month is prorated.',
    confirmDowngrade: 'Switch to Starter? Auto-posting, social accounts and auto-import will be disabled. The price difference is credited to you.',
    confirmCancel: 'Switch to Free? Your subscription stays fully active until the end of the paid period{{date}} and then ends. You can undo this any time before then.',
    cancelScheduledToast: 'Cancellation scheduled. Everything stays active until the period ends.',
    cancelFailed: 'Cancellation failed.',
    resumed: 'Subscription resumed. Glad you are staying!',
    resumeFailed: 'Could not resume the subscription.',
    features: {
      freeVideos: '5 videos / month',
      freeQuality: '720p video quality',
      freeTemplates: 'Basic templates',
      freeSupport: 'Email support',
      starterVideos: '5 videos / month, created on demand',
      starterNoAutopost: 'No auto-posting: that is what the Autopilot plan is for',
      starterImports: 'Listing import from Justimmo & Willhaben',
      voiceMusic: 'AI voiceover & music in every video',
      // Lead bullets sell the outcome (automatic). Volume / quality /
      // templates are table-stakes underneath. Mirrors the DE ordering.
      autoPost: 'Posts daily on autopilot: Instagram, TikTok, Facebook, YouTube, LinkedIn & X',
      zeroEffort: 'Zero effort: a 12h preview email, you just approve',
      autoImport: 'New listings imported automatically',
      unlimitedVideos: '30+ videos per month',
      hdQuality: 'Full HD 1080p quality',
      allTemplates: 'All premium templates',
      branding: 'Your branding (logo, photo, brand)',
      socialMedia: 'Social media integration',
      prioritySupport: 'Priority support',
      support: 'Priority support',
    },
  },
  landing: {
    // Hero. Three short clauses, rendered on three lines with the
    // last clause getting the violet→teal gradient. Mirrors the German
    // "Ein Inserat. Sechs Plattformen. Null Aufwand." structure: input,
    // reach, effort. Entire USP visible in 2 seconds.
    hero: 'From listing to professional property video. In 30 seconds.',
    // Subtitle backs the slogan up with the mechanism: paste a link,
    // get a finished branded video, optionally full auto-posting on top.
    heroSubtitle: 'Paste a Willhaben link, get a finished video with AI voiceover, music and your branding. And if you want, we post daily to Instagram, TikTok, Facebook, YouTube, LinkedIn and X automatically.',
    startFree: 'Start for Free',
    watchDemo: 'Watch Demo',
    // Numbered section kickers (drafting-sheet labels, v1.8 redesign)
    heroFrameCaption: 'Rendered straight from the listing. In 30 seconds.',
    kickerExamples: 'Examples',
    kickerWhy: 'Why us',
    kickerCompare: 'The comparison',
    kickerHow: 'How it works',
    kickerPricing: 'Pricing',
    kickerStart: 'Start',
    features: 'Features',
    howItWorks: 'How It Works',
    testimonials: 'What Our Customers Say',
    aiPowered: 'For agents who should be posting daily. But never have time',
    // Benefit-led features. Each title is a mini-promise; each
    // description quietly names the mechanism behind it.
    feature1Title: 'Straight from your CRM. No copy-paste.',
    feature1Desc: 'Connect Justimmo or Willhaben once. We pull listings, photos and descriptions automatically. You type nothing.',
    feature2Title: 'Captions that sound like you.',
    feature2Desc: 'The AI learns your writing style from your existing posts. Captions sound like you. Not like a marketing bot.',
    feature3Title: 'Videos that look agency-made.',
    feature3Desc: 'Story, Reel and Feed templates with your logo, brand colors and an AI voiceover in six natural voices.',
    feature4Title: 'Six platforms. One click.',
    feature4Desc: 'Instagram, TikTok, Facebook, YouTube, X and LinkedIn. Posted from one dashboard. 12-hour preview email before every post.',
    seeCreations: 'What your videos look like',
    realExamples: 'Nine properties, nine templates. Every video was created fully automatically from the listing photos.',
    galleryTemplate: 'Template {{name}}',
    everythingYouNeed: 'Why agents stay with us',
    forProfessionals: 'Four things no DIY tool and no agency delivers in this combination',
    howItWorksTitle: 'Set up in 5 minutes. Then it runs.',
    threeSteps: 'Three steps. And you never have to squeeze in a "quick post" between viewings again',
    step1Title: 'Connect your source',
    step1Desc: 'Pair Justimmo, Willhaben or your CRM in two clicks. Your listings land in the account automatically.',
    step2Title: 'Set your style',
    step2Desc: 'Upload your logo, pick your brand colors, import a few of your old posts. The AI learns your writing style.',
    step3Title: 'Let it run',
    step3Desc: 'We render a video every day and email you a preview 12 hours ahead. You click "Approve". Posted.',
    // ── Alternatives comparison (DIY / Agency / Real Content) ────
    // Anchors the decision: visitor already knows what we do, now
    // they need to see why we beat their two existing options.
    // Honest about each path's trade-offs.
    compareTitle: 'DIY, agency or Real Content?',
    compareSubtitle: 'You have three ways to stay visible. Here is the honest comparison.',
    compareDiyTitle: 'Do it yourself',
    compareDiyPrice: '€0 / month',
    compareDiyTime: '4–6 hours per week',
    compareDiyCon1: 'You spend evenings editing instead of meeting clients',
    compareDiyCon2: 'Posts often look thrown-together',
    compareDiyCon3: 'Reach stays low because you can\'t post daily',
    compareAgencyTitle: 'Social-media agency',
    compareAgencyPrice: '€500–2,000 / month',
    compareAgencyTime: '2–3 review calls per month',
    compareAgencyCon1: 'You still write briefings and approve revisions',
    compareAgencyCon2: 'The agency doesn\'t know your listings. The workflow stays slow',
    compareAgencyCon3: 'When the contract ends, the whole routine collapses',
    compareRcTitle: 'Real Content',
    compareRcBadge: 'Our recommendation',
    compareRcPrice: 'from €29 / month',
    compareRcTime: '5-minute setup. Then fully automatic.',
    compareRcPro1: 'Reads straight from Justimmo / Willhaben. You type nothing',
    compareRcPro2: 'A new video every day, automatically on six platforms',
    compareRcPro3: '12-hour preview email. You stay in control',
    pricing: 'Pricing',
    simplePricing: 'Fair pricing. No fine print.',
    startFreeUpgrade: 'Start free, upgrade whenever. If ever',
    perfectStart: 'To try it out',
    forSerious: 'Posting runs on its own',
    mostPopular: 'Most Popular',
    getStartedFree: 'Get Started Free',
    startPro: 'Start Pro',
    readyToTransform: 'Ready to post your listings. Without posting them yourself?',
    joinProfessionals: 'Start free. No credit card. Your first video runs in 5 minutes.',
    noCreditCard: 'No credit card needed for Free',
    // Risk-reversal pills under the hero CTA.
    trustNoCard: 'No credit card needed for Free',
    trustFreeForever: '5 free videos every month',
    trustCancel: 'Cancel anytime',
    tapToUnmute: 'Tap a video to hear the AI voiceover',
    signIn: 'Sign In',
    getStarted: 'Get Started',
    privacy: 'Privacy',
    terms: 'Terms',
    contact: 'Contact',
    // ── v1.5. Pricing section on the landing page ──────────────
    pricingSubtitle: 'Billed monthly. Cancel anytime. No minimum term.',
    // Trust strip badges (4 in a row above the pricing cards)
    trustMonthly: 'Monthly billing',
    trustMonthlyDetail: 'No annual contracts.',
    trustCancelAnytime: 'Cancel anytime',
    trustCancelAnytimeDetail: 'One click in the customer portal.',
    trustDsgvo: 'GDPR-compliant',
    trustDsgvoDetail: 'Servers in the EU. Data export anytime.',
    trustStripe: 'Secure payments',
    trustStripeDetail: 'Handled by Stripe (PCI-DSS Level 1).',
    // Starter card (CTA + features) — v1.8 casual tier
    startStarter: 'Choose Starter',
    forOccasional: 'Create videos, post them yourself',
    starterFeature1: '5 videos per month',
    starterFeature2: 'All templates including premium',
    starterFeature3: 'Listing import from Justimmo & Willhaben',
    starterFeature4: 'AI voiceover, music & your branding',
    starterFeature5: 'Full HD 1080p',
    // Solo card (CTA + features)
    startSolo: 'Start Autopilot',
    soloFeature1: 'One video daily, auto-created and auto-posted',
    soloFeature2: 'Auto-import of new listings from Justimmo & Willhaben',
    soloFeature3: 'Posts on its own across 6 platforms',
    soloFeature4: '12-hour preview: you just approve',
    soloFeature5: 'Priority support',
    // Team card (CTA + features + savings hint)
    startTeam: 'Start Team',
    teamBadge: 'Agency',
    forAgencies: 'Autopilot for the whole office',
    teamPriceHint: '2 seats included · each additional +€89',
    teamFeature1: 'Everything from Autopilot, for every agent',
    teamFeature2: 'Each agent has their own listing import',
    teamFeature3: 'Add members anytime. Billed pro-rata',
    teamFeature4: 'One invoice for the whole agency',
    teamSavingsTitle: 'Example: 4 agents',
    teamSavingsBody: '4× Autopilot = €396 / month · Team = €357 / month. Save €39 / month (€468 / year).',
    // Inline FAQ under the cards
    pricingFaqTitle: 'Common questions before you sign up',
    pricingFaq1Q: 'Can I cancel anytime?',
    pricingFaq1A: 'Yes. One click in the Stripe billing portal. You keep access until the end of the paid period, then automatically drop to Free.',
    pricingFaq2Q: 'What happens to my videos if I cancel?',
    pricingFaq2A: 'All your previously-rendered videos stay in your account and can be downloaded anytime. Cancelling only stops new videos from being created automatically.',
    pricingFaq3Q: 'Do I need a credit card for Free?',
    pricingFaq3A: "No. Free renders only require a verified email address. No card, no Stripe data. A real payment method only enters the picture when you upgrade to Solo.",
    pricingFaq4Q: 'Can I switch from Autopilot to Team?',
    pricingFaq4A: 'Yes. Cancel Autopilot in the portal, then start a Team plan. Your properties and videos stay with you.',
    pricingFaqMorePrefix: 'More questions?',
    pricingFaqMoreLink: 'See the complete FAQ',
    // ── v1.7. Viral-demo paste-bar (hero) ────────────────────────
    demoPlaceholder: 'Paste a Willhaben URL…',
    demoCta: 'Generate reel',
    demoLoading: 'Rendering…',
    demoHint: 'Free · No account needed · Ready in ~60 seconds',
    demoOr: 'or',
    demoErrorGeneric: 'Something went wrong. Please try again.',
    // Fallback only. The backend ships the funnel-tuned German
    // 429 detail directly on the response. This kicks in only if the
    // response detail is missing (network blip, etc.).
    demoErrorRateLimit: "You've reached your demo limit. Create a free account to generate more videos. Without the demo watermark.",
    demoQuotaSignupCta: 'Create a free account',
  },
  // v1.7. Viral demo share page (/demo/:token).
  demo: {
    loading: 'Loading demo…',
    errorTitle: 'Something is off here',
    errorLoad: 'This demo could not be loaded.',
    backToLanding: 'Back to homepage',
    previewBadge: 'Your demo reel',
    fallbackTitle: 'Your listing as a reel',
    renderingTitle: "We're rendering your reel…",
    renderingHint: 'Voiceover, animations, branding. About 60 seconds.',
    renderingButton: 'Rendering…',
    queuedHint: 'Listing imported. Render in progress. Once your video is ready you can save it to your account for free.',
    failedTitle: 'Render failed',
    failedBody: 'Try again with a different listing.',
    failedRetryHint: 'Willhaben is sometimes slow to respond. Try a different listing.',
    tryAnother: 'Try a different listing',
    tapHint: 'Tap the video to hear the voiceover',
    saveTitle: 'Save this reel to your account',
    saveBody: 'Create a free account in 30 seconds and the video shows up in your dashboard.',
    signupCta: 'Create a free account',
    claimNow: 'Save to my account now',
    signupHint: 'No credit card · 5 free renders per month',
    unlockTemplates: 'Try other templates',
    unlockTemplatesBody: '11 templates. Story, Reel, Feed post.',
    unlockAutoPost: 'Auto-post on 6 platforms',
    unlockAutoPostBody: 'Instagram, TikTok, Facebook, YouTube, LinkedIn, X.',
    footerPitch: 'This video was generated automatically from a Willhaben listing. By Real Content. Connect Justimmo or Willhaben and we make one for you every day.',
    footerLearnMore: 'Learn more',
    claimSuccess: 'Your demo video has been added to your account.',
  },
  // v1.5. Team subscription management UI (TeamPage + invite flow).
  team: {
    startTitle: 'Start a Team',
    startSubtitle: 'Multiple agents under one bill. Cheaper than separate Solo plans.',
    startCardTitle: 'Set up your team subscription',
    startCardSubtitle: "Pick how many seats to start with. You can add or remove members anytime. We'll prorate the next invoice.",
    nameLabel: 'Team name',
    namePlaceholder: 'Smith Real Estate',
    seatsLabel: 'Seats (min. 2)',
    fewerSeats: 'Fewer seats',
    moreSeats: 'More seats',
    priceLabel: 'Monthly',
    priceBase: 'for the first 2 seats',
    priceVsSolo: 'vs. {{solo}}× Solo (€{{solo}}). Save €{{save}}',
    startCta: 'Start Team subscription',
    checkoutHint: 'Stripe Checkout. Secure, cancel anytime.',
    checkoutFailed: "Couldn't start checkout.",
    defaultName: 'My Team',
    subtitle: '{{count}} seats · monthly billing',
    youAreOwner: "You're the owner",
    leaveCta: 'Leave team',
    confirmLeave: "Leave the team? You'll lose access to team features right away.",
    leftTeam: "You've left the team.",
    membersTitle: 'Members',
    membersSubtitle: '{{count}} of {{max}} seats filled',
    ownerLabel: 'Owner',
    noMembers: 'No members yet.',
    confirmRemove: 'Remove {{who}} from the team?',
    memberRemoved: 'Member removed.',
    removeFailed: 'Removal failed.',
    inviteTitle: 'Invite member',
    inviteSubtitle: "Enter an email. We'll send an invitation. Accepting auto-adds a seat (billed pro-rata).",
    invitePlaceholder: 'colleague@firm.com',
    inviteCta: 'Invite',
    inviteSent: 'Invitation sent.',
    inviteSentNoEmail: 'Invitation created but email failed to send.',
    inviteFailed: 'Invitation failed.',
    pendingTitle: 'Pending invitations',
    expiresAt: 'expires: {{date}}',
    cancelInvite: 'Revoke',
    billingHint: 'Billing, payment method, and cancellation are managed via the Stripe billing portal. See the pricing page.',
    toPricing: 'To billing',
    loadFailed: 'Could not load team.',
  },
  // v1.5. First-render onboarding (replaces the old 7-step wizard).
  firstRender: {
    heroBadge: '60-second test drive',
    heroTitle: 'Make your first video, right now.',
    heroSubtitle: "Pick a template. We'll render it with our sample listing. No uploads, no settings.",
    skip: 'Skip for now',
    demoBadge: 'Sample',
    pickTemplate: 'Pick a template',
    clickHint: 'One click. Render starts immediately.',
    useTemplate: 'Render this one',
    templateCount: '{{count}} templates available',
    noTemplatesForProperty: "No templates match your image count. Upload a few more photos and they'll unlock.",
    orUploadOwnPrefix: 'Want to try with your own listing instead?',
    orUploadOwnLink: 'Create your own property',

    renderingTitle: 'Working on your video…',
    hype1: 'AI is picking the best shots…',
    hype2: 'German-language voiceover being generated…',
    hype3: 'Animations + branding being assembled…',
    hype4: 'Final polish. Almost done…',

    doneTitle: "Magic, isn't it?",
    doneSubtitle: 'This is your first Real Content video. Fully automatic.',
    // Demo-render variant copy. Shown when the backend returns
    // is_first_render=true on /generate/video.
    demoTitle: 'That was your demo.',
    demoSubtitle: 'Make it yours. Add your photo and branding so your next 5 videos feature you, not our demo agent.',
    demoBadge: 'Demo',
    demoQuotaHint: "Demo doesn't count. You still have all 5 Free renders left.",
    profileNudgeTitle: 'Your photo and branding are still missing',
    profileNudgeBody: 'The demo shows our default agent photo. Upload your profile picture, logo and phone number. Then every one of your 5 Free videos will feature you.',
    profileNudgeCta: 'Complete profile',
    nextTitle: "What's next?",
    nextSubtitle: "Here's what it looks like with your real listings.",
    ladderJustimmoTitle: 'Connect Justimmo',
    ladderJustimmoBody: 'Auto-import your real listings. One new video every day, posted hands-free.',
    ladderJustimmoCta: 'Learn more',
    ladderAutoPostTitle: 'Activate auto-posting',
    ladderAutoPostBody: 'AI posts daily on your behalf to Instagram, TikTok & Co. With preview email.',
    ladderAutoPostCta: 'See Solo',
    ladderTeamTitle: 'Start a team',
    ladderTeamBody: 'Multiple agents under one account. From €179 / month.',
    ladderTeamCta: 'Team plan',
    toDashboard: 'To Dashboard',
    freeQuotaHint: "You have 4 more Free renders left. Try other templates!",

    bootstrapFailed: "Couldn't load the demo. Please reload the page.",
    renderFailed: "Couldn't start the render.",
    renderTimeout: 'Render is taking longer than expected. Please reload.',
    statusCheckFailed: "Couldn't check the render status.",
    reload: 'Reload',

    // Mode toggle (sample vs your own)
    modeDemo: 'Use sample',
    modeManual: 'Your own photos',
    yourProperty: 'Your property',
    fillFormFirst: 'Save your property above first.',
    noPropertyYet: 'Pick or create a property first.',

    // Manual-property form
    imagesLabel: 'Images',
    uploading: 'Uploading…',
    dragOrClick: 'Drag images here or click',
    imageHint: 'JPG, PNG, WebP · max 5 MB per file · 3-7 images ideal for the templates',
    imagesNeed3: 'At least 3 images',
    imagesOk: '{{count}} uploaded ✓',
    imagesNeedMore: '{{count}} more needed',
    imagesMinThree: 'Please upload at least 3 images. Most templates need a few to choose from.',
    titleLabel: 'Title',
    titleNeed8: 'At least 8 characters',
    titleShort: '{{remaining}} more',
    titleTooShort: 'Title is too short. At least 8 characters so it looks good in the video.',
    titleHelp: 'Appears as the headline and is spoken by the AI voiceover.',
    titlePlaceholder: 'Beautiful family home in Vienna',
    priceLabel: 'Price',
    locationLabel: 'Address',
    highlightsLabel: 'Highlights (comma-separated)',
    highlightsPlaceholder: '4 rooms, 130 m², garden, garage',
    descriptionLabel: 'Description',
    descriptionPlaceholder: "Charming property in a sought-after location… (or click \"Generate with AI\")",
    saveProperty: 'Save and pick a template',
    fillRequired: 'Title, price, and address are required.',
    imagesRequired: 'At least one image is required.',
    propertyCreated: 'Property saved. Now pick a template.',
    propertyFailed: 'Save failed.',
    // Form guidance block at the top of the manual form.
    formGuidanceTitle: "How the video uses your inputs",
    formGuidanceImages: 'Images',
    formGuidanceTitle2: 'Title',
    formGuidancePrice: 'Price + address',
    formGuidanceHighlights: 'Highlights',

    // AI description button
    aiSuggest: 'Generate with AI',
    aiDone: 'AI suggestion inserted',
    aiHint: "AI suggestion is optional. You can edit it after. One generation per session.",
    aiNeedsTitle: 'Type a title first. The AI needs a hint.',
    aiDescriptionDone: "Description generated. Feel free to tweak it.",
    aiFailed: 'AI is unreachable right now.',

    // Willhaben-import mode
    modeWillhaben: 'From Willhaben',
    fromWillhabenBadge: 'From Willhaben',
    willhabenTitle: 'Import a listing from Willhaben',
    willhabenSubtitle: "Paste the link to your Willhaben listing. We'll grab the title, price, address, and all photos automatically. No typing, no uploading.",
    willhabenUrlLabel: 'Willhaben URL',
    willhabenLoadBtn: 'Load listing',
    willhabenHint: 'Must be a Willhaben real-estate listing. E.g. a /iad/immobilien/d/… page. We confirm with you again before importing.',
    willhabenNeedUrl: 'Please paste a Willhaben URL.',
    willhabenFailed: "Couldn't load the Willhaben listing.",
    willhabenChangeUrl: 'Different listing',
    willhabenSelectImages: 'Pick images for the video',
    willhabenSelectedCount: '{{selected}} of {{total}} selected',
    willhabenSelectHint: 'Tip: the first selected image is often used as the cover. Pick a strong exterior shot first.',
    willhabenAgreeText: "I confirm this is my listing (or I'm authorised by the owner) and that I'm allowed to use the photos in the video.",
    willhabenAgreeFirst: "Please confirm this is your listing.",
    willhabenPickThree: 'Please pick at least 3 images for the video.',
    willhabenImportBtn: 'Import and pick a template',
    willhabenImporting: 'Importing…',
    willhabenImported: 'Listing imported. Now pick a template.',
    willhabenImportFailed: 'Import failed. Please try again.',
    willhabenImportFirst: 'Import your Willhaben listing above first.',
    willhabenOpenOriginal: 'Open original',
    willhabenImportedTitle: 'Listing imported!',
    willhabenAskMore: 'Want to also import other listings from the same broker?',
    willhabenImportMore: 'Import more from broker',
    willhabenDone: 'Done',
    editProperty: 'Edit',
  },
  broker: {
    title: 'Bulk import from a broker profile',
    subtitle: "Paste the link to a Willhaben broker profile. E.g. the \"More from this seller\" page. We'll pull every active listing and you pick which to import.",
    urlLabel: 'Broker URL',
    loadBtn: 'Load broker',
    hint: 'Tip: on any of your own Willhaben listings, click "More from this seller". The page that opens is the right URL.',
    needUrl: 'Please paste a Willhaben broker URL.',
    previewFailed: 'Could not load broker data.',
    activeCount: '{{count}} active listings found',
    changeBroker: 'Different broker',
    allCategories: 'All categories',
    searchPlaceholder: 'Search title/location...',
    groupByAgent: 'Group by agent',
    enrichFailed: 'Could not resolve agent names.',
    allAgents: 'All agents',
    unknownAgent: '(No name)',
    selectedCount: '{{count}} selected',
    selectAllVisible: 'Select all (visible)',
    deselectAll: 'Deselect all',
    clearAll: 'Clear selection',
    hiddenSelectedHint: '{{count}} other selected listings are hidden by the filter. They still count when you import.',
    clearFilters: 'Clear filters',
    alreadyImported: 'Already imported',
    noMatch: 'No listings match the current filters.',
    pickAtLeastOne: 'Please select at least one listing.',
    startImport: 'Import {{count}} listings',
    startFailed: 'Could not start import.',
    disclaimer: "Note: by importing you confirm these are your listings (or you're authorized by the owner) and that you may use the photos in videos.",
    running: 'Importing listings...',
    done: 'Import complete',
    cancelled: 'Import cancelled',
    cancelling: 'Cancelling...',
    cancel: 'Cancel',
    errored: 'errors',
    doneHint: '{{count}} listings are now in your library. You can start rendering videos right away.',
    cancelledHint: 'Already-imported listings stay saved.',
  },
  quickstart: {
    title: 'Your first steps',
    subtitle: 'Four steps to fully-automatic posting. Take it at your pace.',
    completed: 'done',
    allDoneTitle: "You're all set!",
    allDoneSubtitle: 'Setup complete. Sit back. The rest runs on its own.',
    finishBtn: "Great. Let's go",
    subtitleFree: 'Your first video and your branding: how to get the most out of Real Content.',
  },
  subSuccess: {
    startSetup: 'Start setup',
    welcomeBodyWizard: 'Your subscription is active. Let us walk through the next 7 steps: profile, data source, templates, voiceover, social media and auto-post. After that everything runs automatically.',
    invalidSession: 'Invalid session.',
    timeoutToast: 'Payment verification is taking unusually long. Please check your email.',
    toast: 'Payment successful. Welcome!',
    expired: 'Payment session has expired.',
    verifyingTitle: 'Confirming payment…',
    verifyingBody: 'One moment, activating your subscription.',
    welcomeTitle: 'Welcome!',
    welcomeBody: "Your plan is active. To give Auto-Post something to work with, connect your data source now (Justimmo or Willhaben). We'll import new listings automatically every day.",
    connectSource: 'Connect data source',
    skipToDashboard: 'Later, go to dashboard',
    errorTitle: "Couldn't verify your payment",
    errorBody: 'If you were charged, email support@real-content.at and we\'ll fix it right away.',
    tryAgain: 'Try again',
    goDashboard: 'Go to dashboard',
  },
  imports: {
    blockedTitle: 'Deleted listings',
    blockedDesc: 'You deleted these listings. They will no longer be imported automatically. "Re-import" releases them again.',
    reimport: 'Re-import',
    title: 'Data sources',
    loadFailedTitle: "Couldn't load status",
    loadFailedBody: "We couldn't fetch your data-source connection status. Please retry. The panel below may be incomplete.",
    retry: 'Retry',
    subtitle: 'Connect where your listings should come from. We import them automatically so they\'re ready for videos and auto-post immediately.',
    connected: 'Connected',
    manage: 'Manage',
    connectJustimmo: 'Connect Justimmo',
    justimmoDesc: 'CRM sync. All your Justimmo listings are available automatically.',
    willhabenDesc: 'Link your broker profile. We scan daily for new listings under your name and import them automatically.',
    willhabenAlsoBulk: 'Tip: under "New property" → "Broker profile" you can also bulk-import all listings of any Willhaben broker on demand. No active subscription required.',
    dupWarningTitle: 'Heads up: both sources active',
    dupWarningBody: 'If the same property is listed on both Justimmo and Willhaben, we\'ll import it twice. Recommendation: pick one as your primary channel. Usually your CRM (Justimmo). And disable the other.',
    account: 'Account',
    scopedTo: 'Listings by',
    imported: 'Imported',
    properties: 'properties',
    helpHeader: 'How does this all fit together?',
    helpRow1: 'Imported listings show up under "Properties" and are immediately ready for videos.',
    helpRow2: 'When auto-post is on, new listings are automatically considered there too.',
    helpRow3: 'Disconnect any source anytime. Already-imported properties stay.',
    toProperties: 'Go to my properties',
  },
  subscription: {
    sectionTitle: 'Willhaben broker profile',
    sectionDesc: 'Link your Willhaben broker profile so new listings get imported automatically every day.',
    connectTitle: 'Link broker profile',
    connectSub: "We scan daily for new listings under your name and import them automatically.",
    idleHint: "Link your Willhaben broker profile. We scan daily for new listings and import them automatically so they're ready for your auto-post.",
    connectCta: 'Link',
    paidOnly: 'Available on Solo / Team only',
    needUrl: 'Please paste a broker URL.',
    previewFailed: "Couldn't load broker.",
    loadAgents: 'Load broker',
    foundAgents: '{{agentCount}} agents · {{listingCount}} listings',
    pickYourName: 'Which agent are you?',
    suggested: 'Probably you',
    listingsShort: 'lstg.',
    pickAgentFirst: 'Please select an agent.',
    saveBtn: 'Link',
    saveFailed: 'Save failed.',
    scopeHint: "We'll only import this agent's listings going forward. Listings from colleagues under the same broker are ignored.",
    linked: 'Broker profile linked.',
    scopedTo: 'Listings by',
    disconnect: 'Disconnect',
    confirmDisconnect: 'Really disconnect the broker profile? Already-imported listings stay.',
    disconnected: 'Disconnected.',
    disconnectFailed: 'Disconnect failed.',
    rescanNow: 'Scan now',
    rescanning: 'Scanning...',
    rescanFound: '{{count}} new listings imported.',
    rescanNoNew: 'No new listings found.',
    rescanFailed: 'Scan failed.',
    lastScan: 'Last scan',
    neverScanned: 'never',
    lastAddedCount: '{{count}} new listings on last scan',
    noNewLast: 'No new listings on last scan',
    andMore: 'and {{count}} more',
    cronHint: 'We scan automatically every day before your auto-post. New listings are mentioned in the preview email.',
  },
  quickEdit: {
    title: 'Tweak listing details',
    intro: "These fields appear in the video. You can adjust them anytime. Image management lives on the property's detail page.",
    titleLabel: 'Title',
    titleHelp: 'Headline + AI voiceover hook.',
    titleTooShort: 'Title is too short.',
    priceLabel: 'Price',
    locationLabel: 'Location',
    highlightsLabel: 'Highlights (comma-separated)',
    descriptionLabel: 'Description',
    descriptionHelp: 'Used as the basis for the AI voiceover. Shorter + more concrete is usually better.',
    saveBtn: 'Save',
    saved: 'Changes saved.',
    saveFailed: 'Save failed.',
  },
  teamInvite: {
    title: 'Team invitation',
    success: 'Welcome to the team "{{team}}"!',
    successHelp: "You now have full access to all of Real Content's features via the team plan.",
    toTeam: 'Go to team',
    conflict: "This invitation can't be accepted",
    conflictHelp: "You're already part of a team. Leave it first, or contact the owner.",
    failed: "Couldn't accept the invitation.",
    noToken: "No valid invitation token in the URL. If you opened this from an email, the link may have expired.",
  },
};

export default en;
